/**
 * Funnel performance rules, kept free of Angular so they can be tested on their own:
 * the plain-language insights, the funnel drawing, and the cost per goal chart.
 */
import { CostTrendPoint, formatCredits, FunnelMetricsResponse, FunnelMove, FunnelStageMetric } from './funnel.logic';

export type InsightLevel = 'good' | 'warn' | 'info';

export interface Insight {
  level: InsightLevel;
  title: string;
  text: string;
}

/** A change the loop applied, shown as a marker on the chart. */
export interface ChangeMarker {
  at: number; // epoch seconds
  label: string; // "Call prompt"
  why: string;
  runId?: string;
}

function pct(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !isFinite(ratio as number)) { return '–'; }
  return Math.round(ratio * 100) + '%';
}

/** The move where the most leads fall away (lowest conversion). */
export function biggestDropOff(moves: FunnelMove[]): FunnelMove | null {
  let worst: FunnelMove | null = null;
  for (const m of moves || []) {
    if (m.conversion === null || m.conversion === undefined || !m.reached_from) { continue; }
    if (!worst || (m.conversion as number) < (worst.conversion as number)) { worst = m; }
  }
  return worst;
}

/** The move that costs the most credits per lead moved. */
export function priciestMove(moves: FunnelMove[]): FunnelMove | null {
  let top: FunnelMove | null = null;
  for (const m of moves || []) {
    if (m.credits_per_move === null || m.credits_per_move === undefined) { continue; }
    if (!top || (m.credits_per_move as number) > (top.credits_per_move as number)) { top = m; }
  }
  return top;
}

export interface TrendSummary {
  /** Cumulative cost per goal at the start of the range (first point that had a goal). */
  first: number | null;
  /** Cumulative cost per goal now. */
  latest: number | null;
  /** (latest - first) / first, or null when it cannot be worked out. */
  change: number | null;
  firstDate: string | null;
  latestDate: string | null;
}

export function summariseTrend(points: CostTrendPoint[]): TrendSummary {
  const withCost = (points || []).filter(p => p.cumulative_cost_per_goal !== null && p.cumulative_cost_per_goal !== undefined);
  if (withCost.length === 0) {
    return { first: null, latest: null, change: null, firstDate: null, latestDate: null };
  }
  const first = withCost[0];
  const last = withCost[withCost.length - 1];
  const a = first.cumulative_cost_per_goal as number;
  const b = last.cumulative_cost_per_goal as number;
  return {
    first: a,
    latest: b,
    change: withCost.length > 1 && a > 0 ? (b - a) / a : null,
    firstDate: first.date,
    latestDate: last.date
  };
}

/** How far the cost per goal is from the target: negative is under (good). */
export function targetGap(cost: number | null | undefined, target: number | null | undefined): number | null {
  if (cost === null || cost === undefined || !target || target <= 0) { return null; }
  return (cost - target) / target;
}

/** The plain-language findings shown beside the funnel. Most important first. */
export function buildInsights(
  data: FunnelMetricsResponse | null, target: number | null | undefined, trend: CostTrendPoint[]
): Insight[] {
  const out: Insight[] = [];
  if (!data || !data.configured || !data.summary || !data.stages || !data.moves) { return out; }
  const s = data.summary;

  if (s.goal_outcomes.length === 0) {
    out.push({
      level: 'warn',
      title: 'No goal is set',
      text: 'Pick the outcomes that count as a goal in the project settings. Until then there is no cost per goal to track.'
    });
  } else if (s.goal_leads === 0) {
    out.push({
      level: 'warn',
      title: 'No goals reached yet',
      text: formatCredits(s.total_credits) + ' credits have been spent in this period and no lead has reached a goal.'
    });
  }

  const gap = targetGap(s.cost_per_goal_credits, target);
  if (gap !== null && target) {
    if (gap <= 0) {
      out.push({
        level: 'good',
        title: 'Under your target',
        text: 'A goal costs ' + formatCredits(s.cost_per_goal_credits) + ' credits, ' + pct(Math.abs(gap)) +
          ' below your target of ' + formatCredits(target) + '.'
      });
    } else {
      out.push({
        level: 'warn',
        title: 'Above your target',
        text: 'A goal costs ' + formatCredits(s.cost_per_goal_credits) + ' credits, ' + pct(gap) +
          ' above your target of ' + formatCredits(target) + '.'
      });
    }
  } else if (!target && s.goal_leads > 0) {
    out.push({
      level: 'info',
      title: 'Set a cost per goal target',
      text: 'Add the cost per goal you are aiming for in the project settings, and this page will show how far off you are.'
    });
  }

  const t = summariseTrend(trend);
  if (t.change !== null && Math.abs(t.change) >= 0.03) {
    out.push({
      level: t.change < 0 ? 'good' : 'warn',
      title: t.change < 0 ? 'Cost per goal is coming down' : 'Cost per goal is going up',
      text: 'It moved from ' + formatCredits(t.first) + ' to ' + formatCredits(t.latest) + ' credits (' +
        (t.change < 0 ? '' : '+') + Math.round(t.change * 100) + '%) over this period.'
    });
  }

  const drop = biggestDropOff(data.moves);
  if (drop && (drop.conversion as number) < 0.5) {
    out.push({
      level: 'warn',
      title: 'Biggest drop-off',
      text: 'Only ' + pct(drop.conversion) + ' of leads move from ' + drop.from_name + ' to ' + drop.to_name +
        ' (' + drop.reached_to + ' of ' + drop.reached_from + '). Work on this step first.'
    });
  }

  const dear = priciestMove(data.moves);
  if (dear && dear.credits_per_move !== null && data.moves.length > 1) {
    out.push({
      level: 'info',
      title: 'Most expensive step',
      text: formatCredits(dear.credits_per_move) + ' credits to move one lead from ' + dear.from_name + ' to ' +
        dear.to_name + '.'
    });
  }

  for (const m of data.moves) {
    if (m.recommended_channel === 'whatsapp' && m.whatsapp_credits_per_move !== null && m.voice_credits_per_move) {
      out.push({
        level: 'good',
        title: 'WhatsApp is cheaper here',
        text: m.from_name + ' to ' + m.to_name + ' costs ' + formatCredits(m.whatsapp_credits_per_move) +
          ' credits on WhatsApp against ' + formatCredits(m.voice_credits_per_move) + ' on a call.'
      });
      break;
    }
  }

  if (data.unmapped_outcomes && data.unmapped_outcomes.length) {
    const count = data.unmapped_outcomes.reduce((n, u) => n + u.count, 0);
    out.push({
      level: 'info',
      title: 'Some outcomes are not in the funnel',
      text: count + ' call' + (count === 1 ? '' : 's') + ' ended with an outcome that is not placed in a stage, so ' +
        'they do not move leads. Place them in the project settings.'
    });
  }

  if (data.truncated) {
    out.push({
      level: 'info',
      title: 'Partial numbers',
      text: 'This range has more calls than one request reads. Pick a shorter range for exact figures.'
    });
  }
  return out;
}

// -------------------------------------------------------------------- the funnel drawing

export interface FunnelBand {
  stage: FunnelStageMetric;
  /** Width of the band as a share of the full funnel, never below a readable minimum. */
  width: number;
  /** CSS clip-path that narrows the bottom of the band toward the next stage. */
  clip: string;
  /** Share of the previous stage that reached this one, null for the first. */
  keptFromPrevious: number | null;
  /** Leads lost between the previous stage and this one. */
  lostFromPrevious: number | null;
  move: FunnelMove | null;
}

const MIN_BAND_WIDTH = 26;

/** Bands for the funnel drawing: widest at the top, narrowing with the leads that reach each stage. */
export function funnelBands(stages: FunnelStageMetric[], moves: FunnelMove[]): FunnelBand[] {
  if (!stages || stages.length === 0) { return []; }
  const top = Math.max(stages[0].reached || 0, 1);
  const widths = stages.map(s => Math.max(MIN_BAND_WIDTH, Math.round(((s.reached || 0) / top) * 100)));
  // Never wider than the band above, so it always reads as a funnel.
  for (let i = 1; i < widths.length; i++) {
    widths[i] = Math.min(widths[i], widths[i - 1]);
  }
  return stages.map((stage, i) => {
    const next = i < stages.length - 1 ? widths[i + 1] : widths[i] * 0.82;
    const inset = widths[i] > 0 ? Math.max(0, ((widths[i] - next) / 2 / widths[i]) * 100) : 0;
    const clip = 'polygon(0 0, 100% 0, ' + (100 - inset).toFixed(1) + '% 100%, ' + inset.toFixed(1) + '% 100%)';
    const prev = i > 0 ? stages[i - 1] : null;
    return {
      stage,
      width: widths[i],
      clip,
      keptFromPrevious: prev && prev.reached ? stage.reached / prev.reached : null,
      lostFromPrevious: prev ? Math.max(prev.reached - stage.reached, 0) : null,
      move: i > 0 ? (moves || []).filter(m => m.to_stage === stage.id)[0] || null : null
    };
  });
}

// -------------------------------------------------------------------- the cost per goal chart

export interface ChartDot {
  x: number;
  y: number;
  point: CostTrendPoint;
  /** Hollow dot for the day or week's own cost; the line carries the running figure. */
  hasPeriodCost: boolean;
  periodY: number | null;
}

export interface ChartMarker {
  x: number;
  marker: ChangeMarker;
}

export interface ChartTick {
  y: number;
  label: string;
}

export interface ChartXTick {
  x: number;
  label: string;
}

export interface Chart {
  width: number;
  height: number;
  padLeft: number;
  padRight: number;
  padTop: number;
  padBottom: number;
  line: string;
  area: string;
  dots: ChartDot[];
  markers: ChartMarker[];
  yTicks: ChartTick[];
  xTicks: ChartXTick[];
  targetY: number | null;
  max: number;
}

function dayNumber(ymd: string): number {
  const p = ymd.split('-').map(n => parseInt(n, 10));
  return Math.floor(Date.UTC(p[0], p[1] - 1, p[2]) / 86400000);
}

/** YYYY-MM-DD of an epoch second, in India time. */
export function istDay(epochSeconds: number): string {
  return new Date(epochSeconds * 1000 + 330 * 60000).toISOString().slice(0, 10);
}

function niceMax(value: number): number {
  if (value <= 0) { return 10; }
  const pow = Math.pow(10, Math.floor(Math.log(value) / Math.LN10));
  const n = value / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function shortDate(ymd: string): string {
  const p = ymd.split('-');
  return parseInt(p[2], 10) + ' ' + MONTHS[parseInt(p[1], 10) - 1];
}

/**
 * Geometry for the cost per goal chart. The line is the running cost per goal
 * (total credits so far divided by goals so far), which is the figure that shows
 * whether the campaign is getting cheaper. Hollow dots are each day's or week's own cost.
 */
export function buildChart(
  points: CostTrendPoint[], target: number | null | undefined, markers: ChangeMarker[],
  rangeStart: string, rangeEnd: string, width = 760, height = 280
): Chart {
  const padLeft = 52, padRight = 20, padTop = 18, padBottom = 34;
  const innerW = width - padLeft - padRight;
  const innerH = height - padTop - padBottom;
  const startDay = dayNumber(rangeStart);
  const endDay = Math.max(dayNumber(rangeEnd), startDay + 1);
  const span = endDay - startDay;
  const xFor = (ymd: string) => padLeft + (Math.min(Math.max(dayNumber(ymd) - startDay, 0), span) / span) * innerW;

  const values: number[] = [];
  points.forEach(p => {
    if (p.cumulative_cost_per_goal !== null) { values.push(p.cumulative_cost_per_goal as number); }
    if (p.cost_per_goal !== null) { values.push(p.cost_per_goal as number); }
  });
  if (target && target > 0) { values.push(target); }
  const max = niceMax((values.length ? Math.max.apply(null, values) : 10) * 1.1);
  const yFor = (v: number) => padTop + innerH - (Math.min(Math.max(v, 0), max) / max) * innerH;

  const dots: ChartDot[] = [];
  const linePts: string[] = [];
  const areaPts: string[] = [];
  let firstX = 0;
  let lastX = 0;
  points.forEach(p => {
    const x = xFor(p.date);
    const hasPeriodCost = p.cost_per_goal !== null;
    const running = p.cumulative_cost_per_goal;
    if (running !== null) {
      const y = yFor(running as number);
      if (linePts.length === 0) { firstX = x; }
      lastX = x;
      linePts.push(x.toFixed(1) + ',' + y.toFixed(1));
      areaPts.push(x.toFixed(1) + ',' + y.toFixed(1));
      dots.push({
        x, y, point: p, hasPeriodCost, periodY: hasPeriodCost ? yFor(p.cost_per_goal as number) : null
      });
    }
  });
  const baseY = padTop + innerH;
  const line = linePts.length ? 'M' + linePts.join(' L') : '';
  const area = linePts.length > 1
    ? 'M' + firstX.toFixed(1) + ',' + baseY + ' L' + areaPts.join(' L') + ' L' + lastX.toFixed(1) + ',' + baseY + ' Z'
    : '';

  const yTicks: ChartTick[] = [];
  for (let i = 0; i <= 4; i++) {
    const v = (max / 4) * i;
    yTicks.push({ y: yFor(v), label: formatCredits(v) });
  }
  const xTicks: ChartXTick[] = [];
  const count = Math.min(6, span + 1);
  for (let i = 0; i < count; i++) {
    const dayOffset = count === 1 ? 0 : Math.round((span / (count - 1)) * i);
    const d = new Date((startDay + dayOffset) * 86400000).toISOString().slice(0, 10);
    xTicks.push({ x: xFor(d), label: shortDate(d) });
  }

  const chartMarkers: ChartMarker[] = [];
  (markers || []).forEach(m => {
    const day = istDay(m.at);
    if (day >= rangeStart && day <= rangeEnd) {
      chartMarkers.push({ x: xFor(day), marker: m });
    }
  });
  return {
    width, height, padLeft, padRight, padTop, padBottom, line, area, dots, markers: chartMarkers,
    yTicks, xTicks, targetY: target && target > 0 ? yFor(target) : null, max
  };
}
